/**
 * Agency share links for one event.
 *
 * Staff pick an agency and a channel, and get back a short address to hand
 * over. Everything the agency does with it comes back here as two numbers that
 * mean different things: **clicks** is traffic, **registrations** is results.
 * The table is sorted by neither — it is newest first, because staff come here
 * to copy a link far more often than to read a report.
 *
 * Disabling is the normal way to retire a link. A link may be printed on a
 * poster that is already in the world, so a disabled link still opens the event
 * page; it just stops counting and stops crediting.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  createEventAgencyLink,
  deleteEventAgencyLink,
  getAgents,
  getEventAgencyLinkQr,
  getEventAgencyLinks,
  updateEventAgencyLink,
} from '@/lib/api';
import { formatDateTime } from '@/lib/eventStudio';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import AgencyCombobox from '@/components/admin/AgencyCombobox';
import {
  StatusPill, adminInputClass, tableCardClass, tableCellClass, tableHeadCellClass,
  tableHeaderRowClass, tableRowClass, tableScrollClass,
} from '@/components/admin/AdminTableStyles';
import {
  Copy, Loader2, Plus, Power, QrCode, RefreshCw, Trash2,
} from 'lucide-react';

const inputClass = adminInputClass;

function agencyLabel(agent) {
  return agent?.company_name || agent?.preferredName || agent?.agent_name || agent?.id || '';
}

function agencyOption(agent) {
  const label = agencyLabel(agent);
  return {
    id: agent?.id,
    label,
    detail: agent?.preferredName && agent.preferredName !== label ? agent.preferredName : '',
    searchText: [
      agent?.company_name, agent?.preferredName, agent?.agent_name,
      agent?.email, agent?.mainContact?.name, agent?.mainContact?.email, agent?.id,
    ].filter(Boolean).join(' ').toLowerCase(),
  };
}

/** A conversion rate only means something once somebody has clicked. */
function conversionLabel(link) {
  const clicks = link.unique_click_count || link.click_count || 0;
  if (!clicks) return '—';
  return `${link.conversion_rate ?? 0}%`;
}

function SummaryTile({ label, value, hint }) {
  return (
    <div className="rounded-lg border border-[rgba(6,43,26,0.10)] bg-white px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[#062B1A]/50">{label}</p>
      <p className="mt-1 text-xl font-semibold text-[#062B1A]">{value}</p>
      {hint && <p className="mt-0.5 text-[11px] text-[#062B1A]/45">{hint}</p>}
    </div>
  );
}

export default function EventAgencyLinks({ eventId, canManage = true }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [agents, setAgents] = useState([]);

  const [agentId, setAgentId] = useState('');
  const [channel, setChannel] = useState('email');
  const [label, setLabel] = useState('');
  const [campaign, setCampaign] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [creating, setCreating] = useState(false);

  const [busyId, setBusyId] = useState('');
  const [qr, setQr] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data: payload } = await getEventAgencyLinks(eventId);
      setData(payload);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not load the agency links');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getAgents()
      .then(({ data: payload }) => setAgents(Array.isArray(payload) ? payload : (payload?.items || [])))
      .catch(console.error);
  }, []);

  const agencyOptions = useMemo(() => agents.map(agencyOption), [agents]);
  const channels = data?.channels || [];
  const items = data?.items || [];
  const summary = data?.summary || {};
  const leaderboard = data?.leaderboard || [];

  const copy = (value, message) => {
    if (!value) return;
    navigator.clipboard?.writeText(value);
    toast.success(message);
  };

  const create = async () => {
    if (!agentId) {
      toast.error('Pick an agency first');
      return;
    }
    try {
      setCreating(true);
      const { data: link } = await createEventAgencyLink(eventId, {
        agent_id: agentId,
        channel,
        label: label.trim(),
        ...(campaign.trim() ? { utm_campaign: campaign.trim() } : {}),
      });
      copy(link.share_url, `Link for ${link.agency_name} copied`);
      setLabel('');
      setCampaign('');
      await load();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not generate the link');
    } finally {
      setCreating(false);
    }
  };

  const toggleStatus = async (link) => {
    try {
      setBusyId(link.id);
      await updateEventAgencyLink(eventId, link.id, {
        status: link.status === 'active' ? 'disabled' : 'active',
      });
      await load();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not update the link');
    } finally {
      setBusyId('');
    }
  };

  const showQr = async (link) => {
    try {
      setBusyId(link.id);
      const { data: blob } = await getEventAgencyLinkQr(eventId, link.id);
      setQr({ link, url: URL.createObjectURL(blob) });
    } catch (error) {
      console.error(error);
      toast.error('Could not render the QR code');
    } finally {
      setBusyId('');
    }
  };

  const closeQr = () => {
    if (qr?.url) URL.revokeObjectURL(qr.url);
    setQr(null);
  };

  const remove = async (link) => {
    try {
      setBusyId(link.id);
      await deleteEventAgencyLink(eventId, link.id, link.has_traffic ? { force: true } : undefined);
      toast.success('Link deleted');
      setConfirmDelete(null);
      await load();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.detail || 'Could not delete the link');
    } finally {
      setBusyId('');
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 py-10 text-sm text-[#062B1A]/60">
        <Loader2 className="size-4 animate-spin" /> Loading agency links…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-[#062B1A]/70">
        Generate one tracked link per agency and per channel, then hand it over. Everyone who opens
        it lands on this event&rsquo;s own page; what comes back is how many people clicked and how
        many of them registered. An agency that mails a list <em>and</em> prints a poster should get
        two links — that is the only way to tell which one worked.
      </p>

      {/* ─── Generator ─── */}
      <Card className="border border-[rgba(6,43,26,0.10)] shadow-sm">
        <CardContent className="space-y-4 p-4">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-[#062B1A]">Agency</Label>
              <AgencyCombobox
                options={agencyOptions}
                value={agentId}
                onChange={setAgentId}
                disabled={!canManage}
                placeholder="Choose an agency"
                emptyLabel="No agency"
                triggerTestId="agency-link-agency"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-[#062B1A]">Channel</Label>
              <Select value={channel} onValueChange={setChannel} disabled={!canManage}>
                <SelectTrigger className={inputClass} data-testid="agency-link-channel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {channels.map((item) => (
                    <SelectItem key={item.id} value={item.id}>{item.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="agency-link-label" className="text-xs font-semibold text-[#062B1A]">
                Label <span className="font-normal text-[#062B1A]/45">(optional)</span>
              </Label>
              <Input
                id="agency-link-label"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="August newsletter"
                disabled={!canManage}
                className={inputClass}
              />
            </div>
            <div className="flex items-end">
              <Button
                type="button"
                onClick={create}
                disabled={!canManage || creating || !agentId}
                data-testid="generate-agency-link"
                className="w-full bg-[#062B1A] text-white hover:bg-[#041B10]"
              >
                {creating
                  ? <Loader2 className="mr-2 size-4 animate-spin" />
                  : <Plus className="mr-2 size-4" />}
                Generate &amp; copy
              </Button>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setAdvanced((current) => !current)}
            className="text-xs font-semibold text-[#062B1A]/60 underline-offset-2 hover:underline"
          >
            {advanced ? 'Hide' : 'Show'} campaign tagging
          </button>
          {advanced && (
            <div className="grid grid-cols-1 gap-3 rounded-lg bg-[#F7F8FA] p-3 lg:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="agency-link-campaign" className="text-xs font-semibold text-[#062B1A]">
                  Campaign name
                </Label>
                <Input
                  id="agency-link-campaign"
                  value={campaign}
                  onChange={(event) => setCampaign(event.target.value)}
                  placeholder={data?.event?.title || 'Defaults to this event'}
                  disabled={!canManage}
                  className={inputClass}
                />
              </div>
              <p className="self-end text-xs text-[#062B1A]/55">
                The link is tagged with the agency as the source, the channel as the medium and this
                as the campaign, so it shows up under a name you recognise in any analytics tool.
                Leave it blank to use the event.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Totals ─── */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <SummaryTile label="Links" value={summary.links ?? 0}
          hint={`${summary.active_links ?? 0} active`} />
        <SummaryTile label="Agencies" value={summary.agencies ?? 0} />
        <SummaryTile label="Clicks" value={summary.click_count ?? 0} />
        <SummaryTile label="People" value={summary.unique_click_count ?? 0}
          hint="Repeat visits folded in" />
        <SummaryTile label="Registrations" value={summary.registration_count ?? 0}
          hint={summary.unique_click_count ? `${summary.conversion_rate ?? 0}% of people` : 'No clicks yet'} />
      </div>

      {/* ─── Links ─── */}
      <Card className={tableCardClass}>
        <CardContent className="p-0">
          <div className={tableScrollClass}>
            <Table>
              <TableHeader>
                <TableRow className={tableHeaderRowClass}>
                  <TableHead className={tableHeadCellClass}>Agency</TableHead>
                  <TableHead className={tableHeadCellClass}>Channel</TableHead>
                  <TableHead className={tableHeadCellClass}>Link</TableHead>
                  <TableHead className={tableHeadCellClass}>Clicks</TableHead>
                  <TableHead className={tableHeadCellClass}>People</TableHead>
                  <TableHead className={tableHeadCellClass}>Registered</TableHead>
                  <TableHead className={tableHeadCellClass}>Conversion</TableHead>
                  <TableHead className={tableHeadCellClass}>Last click</TableHead>
                  <TableHead className={tableHeadCellClass}>Status</TableHead>
                  <TableHead className={tableHeadCellClass} />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 && (
                  <TableRow className={tableRowClass}>
                    <TableCell colSpan={10} className="px-4 py-8 text-center text-sm text-[#062B1A]/55">
                      No agency links yet. Pick an agency above to generate the first one.
                    </TableCell>
                  </TableRow>
                )}
                {items.map((link) => (
                  <TableRow key={link.id} className={tableRowClass} data-testid={`agency-link-${link.code}`}>
                    <TableCell className={`${tableCellClass} py-2.5 font-medium text-[#062B1A]`}>
                      {link.agency_name}
                    </TableCell>
                    <TableCell className={`${tableCellClass} py-2.5`}>
                      <span className="block text-[#062B1A]">{link.channel_label}</span>
                      {link.label && (
                        <span className="block text-[11px] text-[#062B1A]/50">{link.label}</span>
                      )}
                    </TableCell>
                    <TableCell className={`${tableCellClass} py-2.5`}>
                      <button
                        type="button"
                        onClick={() => copy(link.share_url, 'Link copied')}
                        className="font-mono text-xs text-[#062B1A] underline-offset-2 hover:underline"
                        title={link.share_url}
                      >
                        {link.code}
                      </button>
                    </TableCell>
                    <TableCell className={`${tableCellClass} py-2.5`}>{link.click_count}</TableCell>
                    <TableCell className={`${tableCellClass} py-2.5`}>{link.unique_click_count}</TableCell>
                    <TableCell className={`${tableCellClass} py-2.5 font-semibold text-[#062B1A]`}>
                      {link.active_registration_count}
                      {link.registration_count !== link.active_registration_count && (
                        <span className="ml-1 text-[11px] font-normal text-[#062B1A]/45">
                          ({link.registration_count} incl. cancelled)
                        </span>
                      )}
                    </TableCell>
                    <TableCell className={`${tableCellClass} py-2.5`}>{conversionLabel(link)}</TableCell>
                    <TableCell className={`${tableCellClass} py-2.5 text-[#062B1A]/60`}>
                      {link.last_click_at ? formatDateTime(link.last_click_at) : '—'}
                    </TableCell>
                    <TableCell className={`${tableCellClass} py-2.5`}>
                      <StatusPill tone={link.status === 'active' ? 'emerald' : 'slate'}>
                        {link.status === 'active' ? 'Active' : 'Disabled'}
                      </StatusPill>
                    </TableCell>
                    <TableCell className={`${tableCellClass} py-2.5`}>
                      <div className="flex items-center justify-end gap-1">
                        <Button type="button" variant="ghost" size="icon" title="Copy link"
                          onClick={() => copy(link.share_url, 'Link copied')}>
                          <Copy className="size-4" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" title="QR code"
                          disabled={busyId === link.id} onClick={() => showQr(link)}>
                          <QrCode className="size-4" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon"
                          title={link.status === 'active' ? 'Disable' : 'Re-enable'}
                          disabled={!canManage || busyId === link.id}
                          onClick={() => toggleStatus(link)}>
                          <Power className="size-4" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" title="Delete"
                          disabled={!canManage || busyId === link.id}
                          onClick={() => setConfirmDelete(link)}>
                          <Trash2 className="size-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {leaderboard.length > 1 && (
        <Card className="border border-[rgba(6,43,26,0.10)] shadow-sm">
          <CardContent className="space-y-2 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.04em] text-[#062B1A]/50">
              By agency
            </p>
            <p className="text-xs text-[#062B1A]/50">
              Ranked by registrations, not traffic: ten people who all register beat a thousand who
              do not.
            </p>
            <div className="divide-y divide-[rgba(6,43,26,0.07)]">
              {leaderboard.map((row) => (
                <div key={row.agent_id} className="flex items-center justify-between gap-4 py-2 text-sm">
                  <span className="min-w-0 truncate font-medium text-[#062B1A]">{row.agency_name}</span>
                  <span className="shrink-0 text-[#062B1A]/60">
                    {row.registration_count} registered · {row.unique_click_count} people ·{' '}
                    {row.links} link{row.links === 1 ? '' : 's'}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end">
        <Button type="button" variant="outline" onClick={load} disabled={loading}
          className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
          <RefreshCw className={`mr-2 size-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* ─── QR ─── */}
      <Dialog open={Boolean(qr)} onOpenChange={(open) => { if (!open) closeQr(); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>QR code</DialogTitle>
            <DialogDescription>
              {qr?.link?.agency_name} — {qr?.link?.channel_label}. Scanning it counts as a click
              from this link, exactly like tapping it.
            </DialogDescription>
          </DialogHeader>
          {qr?.url && (
            <img src={qr.url} alt={`QR code for ${qr.link.code}`} className="mx-auto size-56" />
          )}
          <p className="text-center font-mono text-xs text-[#062B1A]/60">{qr?.link?.share_url}</p>
          <DialogFooter>
            {qr?.url && (
              <Button asChild variant="outline" className="border-[rgba(6,43,26,0.16)] text-[#062B1A]">
                <a href={qr.url} download={`${qr.link.code}.jpg`}>Download JPEG</a>
              </Button>
            )}
            <Button type="button" onClick={closeQr} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Delete ─── */}
      <AlertDialog open={Boolean(confirmDelete)} onOpenChange={(open) => { if (!open) setConfirmDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this link?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.has_traffic
                ? `${confirmDelete?.agency_name} already has ${confirmDelete?.click_count} click(s) and ${confirmDelete?.active_registration_count} registration(s) on this link. Deleting it removes those numbers from every report. Registrations keep their own record of the agency. Disabling it instead retires the link and keeps the history.`
                : 'This link has no traffic yet, so nothing is lost.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove(confirmDelete)}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
